import {Vue, Component} from 'vue-property-decorator'

interface Interface {
  appendData: Function;
}

@Component
export default class StructuredData extends Vue {
  protected getPlainText(html: string, maxLength: number = 5000): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) : text;
  }

  protected getDescription(productData: object): string {
    const raw = productData['short_description'] || productData['description'] || '';
    return this.getPlainText(raw);
  }

  public appendData(productData: object): void {
    const description = this.getDescription(productData);

    const dataOutput: Record<string, any> = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": productData['name'],
      "image": productData['default_image']['url'] || '',
      "sku": productData['sku'],
      "mpn": productData['sku']
    };

    if (description) {
      dataOutput['description'] = description;
    }

    // Add brand if manufacturer exists
    if (productData['manufacturer']) {
      Object.assign(dataOutput, this.getBrandData(productData));
    }

    // Add category if category_paths exists
    const categoryPath = this.getCategoryPath(productData);
    if (categoryPath) {
      Object.assign(dataOutput, { "category": categoryPath });
    }

    // Add color if sw_color exists
    if (productData['sw_color']) {
      Object.assign(dataOutput, { "color": productData['sw_color'] });
    }

    Object.assign(dataOutput, this.getOfferData(productData));
    Object.assign(dataOutput, this.getReviewData(productData));

    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(dataOutput);
    document.head.appendChild(script);
  }

  protected getBrandData(productData: object): object {
    return {
      "brand": {
        "@type": "Brand",
        "name": productData['manufacturer']
      }
    };
  }

  protected getCategoryPath(productData: object): string | null {
    const categoryPaths = productData['category_paths'];
    if (!categoryPaths || !Array.isArray(categoryPaths) || categoryPaths.length === 0) {
      return null;
    }

    // Get the last category path (most specific)
    const lastPath = categoryPaths[categoryPaths.length - 1];
    if (!Array.isArray(lastPath)) {
      return null;
    }

    // Extract labels and join with " > "
    const labels = lastPath
      .filter((item: any) => item && item.label)
      .map((item: any) => item.label);

    return labels.length > 0 ? labels.join(' > ') : null;
  }

  protected getPriceValidUntil(): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  protected getShippingDetails(productData: object): object[] {
    const price = productData['final_price_incl_tax'] || 0;
    const swStock = productData['sw_stock'] || 0;
    const isPpcProduct = productData['is_ppc'] === true;
    const ppcDeliveryNotice = productData['swff_ppc_delivery_notice'];
    const deliveryPromise = productData['swff_delivery_promise'];

    // Determine delivery time based on stock and product type
    let includeDeliveryTime = false;
    let nlMaxDays = 4;

    if (swStock > 0) {
      // In stock: same day shipping (1 day delivery)
      includeDeliveryTime = true;
      nlMaxDays = 1;
    } else if (isPpcProduct && ppcDeliveryNotice !== undefined && ppcDeliveryNotice !== null) {
      // PPC product with delivery notice
      const days = Number(ppcDeliveryNotice);
      if (!isNaN(days) && days <= 4) {
        includeDeliveryTime = true;
        nlMaxDays = days;
      }
    } else if (deliveryPromise !== undefined && deliveryPromise !== null) {
      // Regular product with delivery promise
      const days = Number(deliveryPromise);
      if (!isNaN(days) && days <= 4) {
        includeDeliveryTime = true;
        nlMaxDays = days;
      }
    }

    // NL shipping: free if >= €50, otherwise €6.95
    const nlShippingRate = price >= 50 ? 0 : 6.95;

    // BE shipping: free if >= €100, otherwise €19.95
    const beShippingRate = price >= 100 ? 0 : 19.95;

    const nlShipping: any = {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": nlShippingRate.toFixed(2),
        "currency": "EUR"
      },
      "shippingDestination": {
        "@type": "DefinedRegion",
        "addressCountry": "NL"
      }
    };

    const beShipping: any = {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": beShippingRate.toFixed(2),
        "currency": "EUR"
      },
      "shippingDestination": {
        "@type": "DefinedRegion",
        "addressCountry": "BE"
      }
    };

    // Add delivery time only if conditions are met
    if (includeDeliveryTime) {
      nlShipping.deliveryTime = {
        "@type": "ShippingDeliveryTime",
        "transitTime": {
          "@type": "QuantitativeValue",
          "minValue": 1,
          "maxValue": nlMaxDays,
          "unitCode": "d"
        }
      };

      // BE always has longer delivery time (add 3 days to NL time, max 7)
      beShipping.deliveryTime = {
        "@type": "ShippingDeliveryTime",
        "transitTime": {
          "@type": "QuantitativeValue",
          "minValue": 1,
          "maxValue": Math.min(nlMaxDays + 3, 7),
          "unitCode": "d"
        }
      };
    }

    return [nlShipping, beShipping];
  }

  protected getReturnPolicy(): object {
    return {
      "@type": "MerchantReturnPolicy",
      "applicableCountry": "NL",
      "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
      "merchantReturnDays": 14,
      "returnMethod": "https://schema.org/ReturnByMail",
      "returnFees": "https://schema.org/ReturnShippingFees"
    };
  }

  protected getOfferData(productData: object): object {
    return {
      "offers": {
        "@type": "Offer",
        "url": productData['url'],
        "priceCurrency": "EUR",
        "price": productData['final_price_incl_tax'],
        "priceValidUntil": this.getPriceValidUntil(),
        "itemCondition": "https://schema.org/NewCondition",
        "availability": productData['in_stock'] > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "Allesvoorparket.nl"
        },
        "shippingDetails": this.getShippingDetails(productData),
        "hasMerchantReturnPolicy": this.getReturnPolicy()
      }
    };
  }

  protected getReviewData(productData: object): object {
    return productData['review_count'] > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": (productData['product_rating'] / 100) * 10,
        "reviewCount": productData['review_count'],
        "bestRating": "10",
        "worstRating": "1"
      }
    } : {};
  }
}
